// Control de cantidad: "Agregar" y, ya en el pedido, − cantidad +, del mismo alto. La cantidad también se puede
// escribir (para pedir 25 cajas sin tocar 25 veces). En el catálogo, en 1 el − pasa a ser "quitar"; en el carrito no
// baja de 1 (cada línea tiene su "Quitar" aparte: un solo botón para borrar). Tres estilos: en la tarjeta, liviano
// (contorno del color de la marca, 40 px); en la ficha, destacado (lleno, 48 px); en el carrito, la píldora llena.
import { cx, Icono } from '@reparto/ui';
import { useEffect, useRef, useState } from 'react';
import { MAX_BULTOS } from '../datos/carrito';

export function ControlCantidad({
  valor,
  onCambiar,
  max,
  nombre,
  deshabilitado,
  textoAgregar = 'Agregar',
  alAgregar = 1,
  compacto,
  minimo = 0,
  tarjeta,
  destacado,
}: {
  valor: number;
  onCambiar: (n: number) => void;
  /** Tope por stock (si la empresa no deja pedir más de lo que hay). */
  max?: number | null;
  /** Para las etiquetas de los botones: "Sumar Vaso térmico 180 ml". */
  nombre: string;
  deshabilitado?: boolean;
  textoAgregar?: string;
  /** Cantidad con la que entra al tocar "Agregar" (en "Lo de siempre", la de la última vez). */
  alAgregar?: number;
  compacto?: boolean;
  /** Hasta dónde baja el −: 0 lo quita del pedido (catálogo), 1 lo deja (carrito). */
  minimo?: number;
  /** En la tarjeta del producto: ocupa el ancho, 40 px de alto, "Agregar" con el contorno de la marca. */
  tarjeta?: boolean;
  /** En la ficha del producto: ocupa el ancho, 48 px de alto, "Agregar al carrito" lleno. */
  destacado?: boolean;
}) {
  const tope = Math.min(MAX_BULTOS, max ?? MAX_BULTOS);
  const [texto, setTexto] = useState(String(valor));
  const editando = useRef(false);
  useEffect(() => {
    if (!editando.current) setTexto(String(valor));
  }, [valor]);

  const confirmar = () => {
    editando.current = false;
    const n = Number.parseInt(texto, 10);
    const nuevo = Number.isFinite(n) && n >= 0 ? Math.max(minimo, Math.min(tope, n)) : valor;
    if (nuevo !== valor) onCambiar(nuevo);
    else setTexto(String(valor));
  };

  const alto = tarjeta ? 'h-10' : compacto ? 'h-11' : 'h-12';
  const ancho = tarjeta ? 'w-10' : compacto ? 'w-11' : 'w-12';
  const borde = tarjeta || destacado;

  if (valor <= 0) {
    return (
      <button
        type="button"
        disabled={deshabilitado}
        onClick={() => onCambiar(Math.min(tope, Math.max(1, alAgregar)))}
        aria-label={`${textoAgregar} ${nombre}`}
        className={cx(
          alto,
          'inline-flex shrink-0 items-center justify-center gap-1 rounded-full font-semibold transition-colors select-none',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600',
          tarjeta ? 'border-[1.5px] px-3 text-[15px]' : 'border-2 px-4 text-base',
          borde && 'w-full',
          deshabilitado
            ? 'cursor-not-allowed border-stone-200 bg-stone-100 text-stone-600'
            : destacado
              ? 'border-primary-600 bg-primary-600 text-sobre-marca hover:brightness-95 active:brightness-90'
              : 'border-primary-600 bg-white text-primary-700 hover:bg-primary-50 active:bg-primary-100',
        )}
      >
        {deshabilitado ? null : <Icono nombre={destacado ? 'add_shopping_cart' : 'add'} tamano={tarjeta ? 20 : 22} />}
        {textoAgregar}
      </button>
    );
  }

  const boton = cx(
    ancho,
    alto,
    'grid shrink-0 place-items-center rounded-full transition-colors select-none',
    'disabled:opacity-40 focus-visible:outline-2 focus-visible:-outline-offset-4',
    borde
      ? 'text-primary-700 hover:bg-primary-100 active:bg-primary-200 focus-visible:outline-primary-600'
      : 'hover:bg-black/10 active:bg-black/15 focus-visible:outline-sobre-marca',
  );
  return (
    <div
      role="group"
      aria-label={`Cantidad de ${nombre}`}
      className={cx(
        alto,
        'inline-flex shrink-0 items-center justify-between rounded-full',
        borde
          ? cx('w-full border-primary-600 bg-primary-50 text-primary-800', tarjeta ? 'border-[1.5px]' : 'border-2')
          : 'bg-primary-600 text-sobre-marca shadow-sm',
      )}
    >
      <button
        type="button"
        className={boton}
        onClick={() => onCambiar(valor - 1)}
        disabled={deshabilitado || valor <= minimo}
        aria-label={valor === 1 && !minimo ? `Quitar ${nombre}` : `Restar uno de ${nombre}`}
      >
        <Icono nombre={valor === 1 && !minimo ? 'delete' : 'remove'} tamano={tarjeta ? 20 : 22} />
      </button>
      <input
        type="number"
        inputMode="numeric"
        pattern="[0-9]*"
        min={0}
        max={tope}
        aria-label={`Cantidad de ${nombre}`}
        value={texto}
        disabled={deshabilitado}
        onFocus={(e) => {
          editando.current = true;
          e.currentTarget.select();
        }}
        onChange={(e) => setTexto(e.target.value.replace(/\D/g, '').slice(0, 5))}
        onBlur={confirmar}
        onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
        className={cx(
          compacto || tarjeta ? 'w-10' : 'w-12',
          'h-full min-w-0 bg-transparent text-center font-bold tabular-nums outline-none',
          tarjeta ? 'text-base' : 'text-lg',
          'focus:rounded-lg focus:bg-white focus:text-stone-900',
        )}
      />
      <button
        type="button"
        className={boton}
        onClick={() => onCambiar(valor + 1)}
        disabled={deshabilitado || valor >= tope}
        aria-label={`Sumar uno de ${nombre}`}
      >
        <Icono nombre="add" tamano={tarjeta ? 20 : 22} />
      </button>
    </div>
  );
}
